import {
  KSQG_THRESHOLD_HAZARDOUS_KG,
  LQG_THRESHOLD_ACUTE_HAZARDOUS_KG,
  LQG_THRESHOLD_HAZARDOUS_KG,
  SQG_THRESHOLD_HAZARDOUS_KG,
  TEXAS_CLASS_1_ONLY_KG,
  thresholdPounds,
  VSQG_THRESHOLD_HAZARDOUS_KG,
} from "encamp-shared/src/constants/wasteGeneratorStatusThresholds";

export type WasteThreshold = {
  type: "federal" | "state";
  name: string;
  /** Threshold in pounds, converted exactly from its kg rule */
  threshold: number;
  /** Whether the threshold is solely acute waste or includes other hazardous waste */
  acute: boolean;
};

const FEDERAL_THRESHOLDS: WasteThreshold[] = [
  {
    type: "federal",
    name: "Federal VSQG",
    threshold: thresholdPounds(VSQG_THRESHOLD_HAZARDOUS_KG),
    acute: false,
  },
  {
    type: "federal",
    name: "Federal SQG",
    threshold: thresholdPounds(SQG_THRESHOLD_HAZARDOUS_KG),
    acute: false,
  },
  {
    type: "federal",
    name: "Federal LQG",
    threshold: thresholdPounds(LQG_THRESHOLD_HAZARDOUS_KG),
    acute: false,
  },
  {
    type: "federal",
    name: "Federal LQG acute",
    threshold: thresholdPounds(LQG_THRESHOLD_ACUTE_HAZARDOUS_KG),
    acute: true,
  },
];

const STATE_THRESHOLDS: Record<string, WasteThreshold[]> = {
  CA: [
    {
      type: "state",
      name: "California SQG — includes non-RCRA waste",
      threshold: 0,
      acute: false,
    },
    {
      type: "state",
      name: "California LQG — includes non-RCRA waste",
      threshold: thresholdPounds(LQG_THRESHOLD_HAZARDOUS_KG),
      acute: false,
    },
  ],
  KS: [
    {
      type: "state",
      name: "Kansas CESQG",
      threshold: 0,
      acute: false,
    },
    {
      type: "state",
      name: "Kansas KSQG",
      threshold: thresholdPounds(KSQG_THRESHOLD_HAZARDOUS_KG),
      acute: false,
    },
  ],
  MA: [
    {
      type: "state",
      name: "Massachusetts VSQG — includes waste oil",
      threshold: 0,
      acute: false,
    },
  ],
  MD: [
    {
      type: "state",
      name: "Maryland SQG",
      threshold: 0,
      acute: false,
    },
    {
      type: "state",
      name: "Maryland Generator (Fully Regulated)",
      threshold: thresholdPounds(SQG_THRESHOLD_HAZARDOUS_KG),
      acute: false,
    },
  ],
  ME: [
    {
      type: "state",
      name: "Maine SQG",
      threshold: 0,
      acute: false,
    },
    {
      type: "state",
      name: "Maine SQG+ — up to 1,320 lbs storage",
      threshold: 0,
      acute: false,
    },
    {
      type: "state",
      name: "Maine LQG — includes federal SQG & LQG",
      threshold: thresholdPounds(SQG_THRESHOLD_HAZARDOUS_KG),
      acute: false,
    },
  ],
  MN: [
    {
      type: "state",
      name: "Minnesota MiniQG — less than 100 lbs per year",
      threshold: 0,
      acute: false,
    },
    {
      type: "state",
      name: "Minnesota VSQG — more than 100 lbs per year",
      threshold: 0,
      acute: false,
    },
  ],
  NH: [
    {
      type: "state",
      name: "New Hampshire SQG",
      threshold: 0,
      acute: false,
    },
    {
      type: "state",
      name: "New Hampshire FQG",
      threshold: thresholdPounds(SQG_THRESHOLD_HAZARDOUS_KG),
      acute: false,
    },
  ],
  RI: [
    {
      type: "state",
      name: "Rhode Island CESQG",
      threshold: 0,
      acute: false,
    },
  ],
  // https://www.tceq.texas.gov/downloads/permitting/waste-registration/pub/rg-222.pdf
  TX: [
    // Only applies to Class 1 Industrial Waste e.g. state regulated waste
    // Unclear how we can represent this in the UI
    {
      type: "state",
      name: "Texas Class 1 Only Generator",
      threshold: thresholdPounds(TEXAS_CLASS_1_ONLY_KG),
      acute: false,
    },
    {
      type: "state",
      name: "Texas VSQG",
      threshold: 0,
      acute: false,
    },
  ],
  WA: [
    {
      type: "state",
      name: "Washington SQG — includes dangerous waste",
      threshold: 0,
      acute: false,
    },
    {
      type: "state",
      name: "Washington MQG — includes dangerous waste",
      threshold: thresholdPounds(SQG_THRESHOLD_HAZARDOUS_KG),
      acute: false,
    },
    {
      type: "state",
      name: "Washington LQG — includes dangerous waste",
      threshold: thresholdPounds(LQG_THRESHOLD_HAZARDOUS_KG),
      acute: false,
    },
  ],
};

export function getWasteThresholdsForState(state: string): WasteThreshold[] {
  const stateUpper = state.toUpperCase();
  const stateThresholds = STATE_THRESHOLDS[stateUpper] ?? [];

  return [...FEDERAL_THRESHOLDS, ...stateThresholds].sort((a, b) => {
    if (b.threshold !== a.threshold) {
      return b.threshold - a.threshold;
    }
    return a.type === "federal" ? -1 : 1;
  });
}
